import { NotificationType } from '@buyerflo/core/schemas/notification'
import { useAuth } from '@clerk/react'
import { useMutation } from '@tanstack/react-query'
import Button from 'components/button'
import ColorSchemeToggle from 'components/colorSchemeToggle'
import { LogoutRounded } from 'components/icons'
import SectionHead from 'components/sectionHead'
import { useState } from 'react'
import { SizableText, Switch, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { useUserData } from './auth'
import EsignConnections from './settings/esignSettings'

const notificationTypes: { type: NotificationType; label: string }[] = [
    { type: 'wishlistSubmitted', label: 'New Wishlist' },
    { type: 'formCompleted', label: 'Form Completed' },
]

export default function UserSettings() {
    const { signOut } = useAuth()
    const { user, refreshUser } = useUserData()
    console.log('[UserSettings] user.notificationPreferences:', JSON.stringify(user.notificationPreferences))
    console.log('[UserSettings] full user keys:', Object.keys(user))
    const trpc = useTRPC()

    const [optimistic, setOptimistic] = useState<
        Record<string, { email: boolean; push: boolean }>
    >({})

    const updatePrefs = useMutation(
        trpc.user.updateNotificationPreferences.mutationOptions({
            onSuccess: async (_data, variables) => {
                await refreshUser()
                setOptimistic(prev => {
                    const { [variables.notificationType]: _, ...rest } = prev
                    return rest
                })
            },
            onError: (_error, variables) => {
                setOptimistic(prev => {
                    const { [variables.notificationType]: _, ...rest } = prev
                    return rest
                })
            },
        }),
    )

    const getPrefs = (type: NotificationType) => {
        if (optimistic[type]) return optimistic[type]
        return {
            email: user.notificationPreferences?.[type]?.email ?? true,
            push: user.notificationPreferences?.[type]?.push ?? true,
        }
    }

    const togglePref = (type: NotificationType, channel: 'email' | 'push', value: boolean) => {
        const current = getPrefs(type)
        const next = {
            email: channel === 'email' ? value : current.email,
            push: channel === 'push' ? value : current.push,
        }
        setOptimistic(prev => ({ ...prev, [type]: next }))
        updatePrefs.mutate({
            notificationType: type,
            ...next,
        })
    }

    return (
        <View
            paddingHorizontal="$4"
            $sm={{ paddingHorizontal: '$6' }}
        >
            <SectionHead title="User Settings" />
            <YStack
                gap="$4"
                marginVertical="$6"
            >
                <XStack
                    gap="$2"
                    alignItems="center"
                >
                    <SizableText>Colour Mode: </SizableText>
                    <ColorSchemeToggle />
                </XStack>

                <YStack gap="$3">
                    <SizableText
                        fontWeight="600"
                        fontSize="$5"
                        mt={20}
                    >
                        Notifications
                    </SizableText>
                    <XStack
                        gap="$6"
                        paddingLeft="$1"
                    >
                        <SizableText width={140} />
                        <SizableText
                            width={60}
                            textAlign="center"
                            fontWeight="500"
                        >
                            Email
                        </SizableText>
                        <SizableText
                            width={60}
                            textAlign="center"
                            fontWeight="500"
                        >
                            Push
                        </SizableText>
                    </XStack>
                    {notificationTypes.map(({ type, label }) => {
                        const prefs = getPrefs(type)
                        return (
                            <XStack
                                key={type}
                                gap="$6"
                                alignItems="center"
                                paddingLeft="$1"
                            >
                                <SizableText width={140}>{label}</SizableText>
                                <XStack
                                    width={60}
                                    justifyContent="center"
                                >
                                    <Switch
                                        size="$2"
                                        p={0}
                                        checked={prefs.email}

                                        onCheckedChange={v => togglePref(type, 'email', v)}
                                    >
                                        <Switch.Thumb animation="quick" />
                                    </Switch>
                                </XStack>
                                <XStack
                                    width={60}
                                    justifyContent="center"
                                >
                                    <Switch
                                        size="$2"
                                        p={0}
                                        checked={prefs.push}

                                        onCheckedChange={v => togglePref(type, 'push', v)}
                                    >
                                        <Switch.Thumb animation="quick" />
                                    </Switch>
                                </XStack>
                            </XStack>
                        )
                    })}
                </YStack>

                <YStack gap="$3">
                    <SizableText
                        fontWeight="600"
                        fontSize="$5"
                        mt={20}
                    >
                        E-Signing
                    </SizableText>
                    <SizableText
                        fontSize="$2"
                        color="$gray10"
                    >
                        Choose your signing service and connect the account used to sign forms. These
                        settings are personal to your account.
                    </SizableText>
                    <EsignConnections />
                </YStack>

                <Button
                    theme="red"
                    variant="outlined"
                    width={200}
                    mt={80}
                    onPress={() => signOut({ redirectUrl: '/' })}
                    icon={<LogoutRounded />}
                    animation="quick"
                    hoverStyle={{ backgroundColor: '$backgroundHover' }}
                    pressStyle={{ backgroundColor: '$backgroundPress', opacity: 0.85 }}
                >
                    Logout
                </Button>
            </YStack>
        </View>
    )
}
